import { FC, ReactNode, useMemo, MouseEvent } from "react";
import { useSelector } from "react-redux";

import isEmpty from "lodash/isEmpty";
import head from "lodash/head";
import isNil from "lodash/isNil";

import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import IconButton from "@mui/material/IconButton";

import useCart from "modules/Cart/useCart";

import { selectChainId } from "redux/slices/metamask";

import AvailableAmount from "sections/storesPage/AvailableAmount";

import Image from "components/Image";
import Card from "components/Card";
import CustomLink from "components/CustomLink";
import PriceLabel from "components/PriceLabel";
import BoxLabel from "components/BoxLabel";
import CustomTooltip from "components/CustomTooltip";
import CustomButton from "components/CustomButton";

import { PAYMENT_FIATS } from "enums/paymentTokens";

import { BundleAmounts } from "interfaces/ClientStudio.interface";

import { APP_ROUTES } from "config/routes";

import { dateToUnixTime, formatUTCSeconds } from "utils/date";
import { mapURLSearchParams, slugify } from "utils/query";
import { filterPaymentDetailsByChainId } from "utils/store";
import { getEthereumChainId } from "utils/blockchain";
import { MODEL_BACKGROUND } from "utils/consts";
import { pluralize } from "utils/text";

import { Bundle } from "services/store/Bundle.interface";
import { SaleInfoInterface } from "services/store/sale/saleInfo";

type ProductTileProps = {
  bundle: Bundle & BundleAmounts & { saleInfo: SaleInfoInterface };
  isEmbedded?: boolean;
  imageWidth?: number;
  imageHeight?: number;
  imageUnoptimized?: boolean;
  useRequestOnly?: boolean;
  isPreview?: boolean;
  isImageDisclaimer?: boolean;
  children?: ReactNode;
  isCustomRedirect?: boolean;
  showAddToCart?: boolean;
};

const ProductTile: FC<ProductTileProps> = ({
  bundle,
  imageWidth = 340,
  imageHeight = 390,
  imageUnoptimized = false,
  isPreview,
  isImageDisclaimer = true,
  children,
  isCustomRedirect = false,
  showAddToCart = false,
}) => {
  const {
    id,
    name: title,
    client,
    image: imageSrc,
    paymentDetails,
    requirements,
    constraints,
    publishDate: bundlePublishDate,
    refPrice,
    refToken,
    saleInfo,
    availableAmount,
    totalAmount,
  } = bundle;
  const { isSaleFinished, isStoreOnSale, isBundleOnSale, isSoldOut } = saleInfo;
  const chainId = useSelector(selectChainId);
  const { addMintItemToCart } = useCart();

  const paymentDetailsByChainId = useMemo(
    () => filterPaymentDetailsByChainId(paymentDetails, chainId),
    [paymentDetails, chainId],
  );
  const paymentDetailsRefPrice = useMemo(
    () =>
      paymentDetailsByChainId.find(
        (detail) =>
          detail.refToken === PAYMENT_FIATS.USD ||
          detail.refToken === PAYMENT_FIATS.PLN,
      ),
    [paymentDetailsByChainId],
  );

  const handleAddToCart = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    addMintItemToCart(id);
  };

  return (
    <CustomLink
      noDecoration
      noDecorationOnHover
      href={
        isCustomRedirect
          ? undefined
          : `${
              isPreview
                ? APP_ROUTES.STORE_PRODUCT_PREVIEW
                : APP_ROUTES.STORE_PRODUCT
            }/${id}/${slugify(title)}?${mapURLSearchParams({
              chainId: chainId || getEthereumChainId(),
            })}`
      }
    >
      <Card clickable={!isCustomRedirect} shadow sx={{ height: "100%" }}>
        <Box
          display="flex"
          alignItems="center"
          sx={{
            margin: "0 auto",
            flex: "1 1 auto",
            position: "relative",
            maxWidth: imageWidth,
            height: { xs: imageHeight / 1.1, sm: imageHeight },
            justifyContent: "center",
            mb: { xs: 4, md: 0 },
          }}
        >
          <Image
            src={imageSrc}
            layout="fill"
            objectFit="cover"
            bgColor={bundle.model?.bgColor || MODEL_BACKGROUND}
            unoptimized={imageUnoptimized}
          />

          <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0 }}>
            <BoxLabel
              variant="h5"
              show={
                isImageDisclaimer &&
                ((isSaleFinished && !isSoldOut) ||
                  isSoldOut ||
                  (!isStoreOnSale && !!client.startSellingDate) ||
                  (!isBundleOnSale && !!bundlePublishDate))
              }
            >
              <>
                {isSaleFinished && !isSoldOut && <b>SALE FINISHED</b>}
                {isSoldOut && <b>ITEM IS SOLD OUT</b>}
                {!isStoreOnSale && client.startSellingDate && (
                  <b>
                    SALE STARTS ON{" "}
                    {formatUTCSeconds(
                      dateToUnixTime(client.startSellingDate) / 1000,
                    )}
                  </b>
                )}
                {!isBundleOnSale && bundlePublishDate && (
                  <b>SALE STARTS ON {formatUTCSeconds(bundlePublishDate)}</b>
                )}
              </>
            </BoxLabel>
          </Box>
        </Box>

        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          py={1}
        >
          <Box>
            <Typography component="h5" variant="h5" paragraph margin={0}>
              {title}
            </Typography>

            <AvailableAmount
              availableAmount={availableAmount}
              totalAmount={totalAmount}
            />
          </Box>

          {requirements &&
            !isEmpty(requirements) &&
            constraints &&
            !isEmpty(constraints) && (
              <Box>
                <CustomTooltip
                  content={
                    <IconButton>
                      <WarningAmberIcon color="secondary" />
                    </IconButton>
                  }
                >
                  <>
                    <Typography mb={0.5}>
                      Restrictions to buy this item:
                    </Typography>
                    {requirements.map((requirement) => (
                      <Typography
                        variant="body2"
                        mb={0}
                        paragraph
                        key={`requirement-${requirement.type}`}
                      >
                        {requirement.description}
                      </Typography>
                    ))}
                    {constraints.map((constraint) => (
                      <Typography
                        variant="body2"
                        mb={0}
                        paragraph
                        key={`constraint-${constraint.name}`}
                      >
                        Allowed {pluralize(constraint.value, "item")} per wallet
                      </Typography>
                    ))}
                  </>
                </CustomTooltip>
              </Box>
            )}
        </Box>
        {!isSaleFinished && !isSoldOut && (
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="body2" fontWeight={600}>
                Price
              </Typography>
              <Typography component="h4" variant="h4">
                {!isNil(refPrice) ?? paymentDetailsRefPrice ? (
                  `${refToken === PAYMENT_FIATS.USD ? "$" : "PLN "}${
                    refPrice ?? paymentDetailsRefPrice
                  }`
                ) : (
                  <PriceLabel
                    value={head(paymentDetailsByChainId)?.price}
                    currency={head(paymentDetailsByChainId)?.paymentToken}
                  />
                )}
              </Typography>
            </Box>
            {showAddToCart && (
              <CustomButton
                onClick={handleAddToCart}
                sx={{ width: { xs: 35 }, height: 35 }}
                disabled={!isStoreOnSale}
              >
                <AddShoppingCartIcon />
              </CustomButton>
            )}
          </Box>
        )}
        {children}
      </Card>
    </CustomLink>
  );
};

export default ProductTile;
